import { ApiError } from '../types/errors.js';
/**
 * Централизованный обработчик ошибок API
 */
export class ApiErrorHandler {
    /**
     * Обрабатывает ошибку API и обогащает её контекстом
     * @throws {ApiError} Обогащённая ошибка с контекстом
     */
    static handle(error, context) {
        const timestamp = new Date().toISOString();
        const enrichedContext = { ...context, timestamp };
        // Определяем статус код и сообщение
        let statusCode;
        let errorMessage;
        if (error instanceof Response) {
            statusCode = error.status;
            errorMessage = `HTTP ${error.status}: ${error.statusText}`;
        }
        else if (error instanceof Error) {
            errorMessage = error.message;
        }
        else {
            errorMessage = 'Unknown error occurred';
        }
        // Создаём обогащённую ошибку
        const apiError = new ApiError(`[${context.method} ${context.endpoint}] ${errorMessage}`, {
            statusCode,
            originalError: error,
            context: enrichedContext
        });
        // Логирование для мониторинга (в production можно отправлять в Sentry/DataDog)
        console.error('[ApiErrorHandler]', {
            message: apiError.message,
            statusCode: apiError.statusCode,
            context: enrichedContext,
            stack: apiError.stack
        });
        throw apiError;
    }
    /**
     * Определяет, можно ли повторить запрос
     * @param error - Ошибка для проверки
     * @returns true если ошибка повторяемая (сетевая или 5xx)
     */
    static isRetryable(error) {
        if (error instanceof ApiError) {
            // Не повторяем клиентские ошибки (4xx)
            if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
                // Исключение: 408 (Request Timeout) и 429 (Too Many Requests) можно повторить
                return error.statusCode === 408 || error.statusCode === 429;
            }
            // Повторяем серверные ошибки (5xx) и неопределённые
            return true;
        }
        // Повторяем неизвестные ошибки (скорее всего сетевые)
        return true;
    }
    /**
     * Вычисляет задержку для retry на основе номера попытки
     * Использует exponential backoff с jitter
     * @param attemptIndex - Номер попытки (начинается с 0)
     * @param baseDelay - Базовая задержка в мс (по умолчанию 1000)
     * @param maxDelay - Максимальная задержка в мс (по умолчанию 30000)
     * @returns Задержка в миллисекундах
     */
    static getRetryDelay(attemptIndex, baseDelay = 1000, maxDelay = 30000) {
        // Exponential backoff: delay = baseDelay * 2^attemptIndex
        const exponentialDelay = baseDelay * Math.pow(2, attemptIndex);
        // Добавляем jitter (случайное отклонение ±25%)
        const jitter = exponentialDelay * (0.75 + Math.random() * 0.5);
        // Ограничиваем максимальной задержкой
        return Math.min(jitter, maxDelay);
    }
    /**
     * Извлекает понятное сообщение об ошибке для пользователя
     * @param error - Ошибка
     * @param fallbackMessage - Сообщение по умолчанию
     * @returns Понятное сообщение для отображения пользователю
     */
    static getUserFriendlyMessage(error, fallbackMessage = 'Произошла ошибка при выполнении запроса') {
        if (error instanceof ApiError) {
            // Мапим HTTP коды в понятные сообщения
            switch (error.statusCode) {
                case 400:
                    return 'Неверные данные запроса';
                case 401:
                    return 'Необходима авторизация';
                case 403:
                    return 'Недостаточно прав для выполнения операции';
                case 404:
                    return 'Запрашиваемый ресурс не найден';
                case 408:
                    return 'Превышено время ожидания ответа';
                case 409:
                    return 'Конфликт данных';
                case 429:
                    return 'Слишком много запросов, попробуйте позже';
                case 500:
                    return 'Внутренняя ошибка сервера';
                case 502:
                    return 'Сервер временно недоступен';
                case 503:
                    return 'Сервис временно недоступен';
                case 504:
                    return 'Превышено время ожидания ответа от сервера';
                default:
                    return error.message || fallbackMessage;
            }
        }
        if (error instanceof Error) {
            return error.message;
        }
        return fallbackMessage;
    }
}
